import { createContext } from 'react'
import type {
  LoyaltyCoupon,
  LoyaltyCouponDiscountType,
  LoyaltyCouponDraft,
  LoyaltyCouponListParams,
  LoyaltyCouponListResponse,
  LoyaltyCouponStatus,
  LoyaltyCouponWebCategoryRef,
} from '../../../../types/loyaltyCoupon'
import type { LoyaltyCouponAdapter } from '../adapters/types'

export type LoyaltyCouponViewMode = 'card' | 'list'

// Category filter is now keyed off the real web-category IDs loaded by the
// provider (mirrors the Product list's `filters.category` shape). 'all' means
// "no filter".
export type LoyaltyCouponCategoryFilter = 'all' | string
export type LoyaltyCouponDiscountTypeFilter = 'all' | LoyaltyCouponDiscountType
export type LoyaltyCouponStatusFilter = 'all' | LoyaltyCouponStatus
// Pantry-only 3-way filter for a coupon's loyalty origin. 'non_loyalty' is the
// pantry default (hides system-generated loyalty-point coupons); 'all' is an
// explicit override that shows them too.
export type LoyaltyCouponTypeFilter = 'all' | 'non_loyalty' | 'loyalty_point'

// A POS location the coupon can be scoped to (`location_ids`). `id` is the
// numeric POS location ID (Cova `LocationId` / Greenline `location_id`), not
// the warehouse `_id`.
export interface LoyaltyCouponLocationOption {
  id: number
  name: string
}

// The Coupons tab provider exposes a uniform surface to the UI. Components
// never branch on `storeType` — the adapter (created by the resolver in
// `coupons/adapters/index.ts`) is the only place that knows about per-POS
// endpoints.

export interface LoyaltyCouponsContextValue {
  adapter: LoyaltyCouponAdapter
  storeId: string
  storeType: string

  // ── Server data ─────────────────────────────────────────────────────────────
  pageData: LoyaltyCouponListResponse | undefined
  coupons: LoyaltyCoupon[]
  isLoading: boolean
  isFetching: boolean

  // ── Web categories (from Categories module, shared by filter + modal) ───────
  webCategories: LoyaltyCouponWebCategoryRef[]
  webCategoriesMap: Record<string, LoyaltyCouponWebCategoryRef>
  isWebCategoriesLoading: boolean

  // ── Participating locations / free shipping (modal) ─────────────────────────
  // Store POS locations sorted by name. Empty for store types without
  // location-scoped coupons.
  locationOptions: LoyaltyCouponLocationOption[]
  // Whether the coupon endpoint accepts `location_ids` (Cova / Greenline).
  supportsLocations: boolean
  // Whether the coupon endpoint accepts `free_shipping` (Cova / Greenline /
  // WooCommerce).
  supportsFreeShipping: boolean

  // ── View mode (persisted to localStorage) ───────────────────────────────────
  view: LoyaltyCouponViewMode
  setView: (view: LoyaltyCouponViewMode) => void

  // ── Search & filters ────────────────────────────────────────────────────────
  keyword: string
  setKeyword: (value: string) => void
  categoryFilter: LoyaltyCouponCategoryFilter
  setCategoryFilter: (value: LoyaltyCouponCategoryFilter) => void
  discountTypeFilter: LoyaltyCouponDiscountTypeFilter
  setDiscountTypeFilter: (value: LoyaltyCouponDiscountTypeFilter) => void
  statusFilter: LoyaltyCouponStatusFilter
  setStatusFilter: (value: LoyaltyCouponStatusFilter) => void
  // Whether this is a pantry store — gates the coupon-type segmented control.
  isPantry: boolean
  // Pantry-only loyalty-origin filter. Ignored (and not sent) for non-pantry.
  couponTypeFilter: LoyaltyCouponTypeFilter
  setCouponTypeFilter: (value: LoyaltyCouponTypeFilter) => void

  // ── Pagination ──────────────────────────────────────────────────────────────
  listParams: LoyaltyCouponListParams
  currentPage: number
  totalPages: number
  totalItems: number
  canGoBack: boolean
  canGoNext: boolean
  showingStart: number
  showingEnd: number
  goToPrevPage: () => void
  goToNextPage: () => void
  goToPage: (page: number) => void

  // ── Modal (create + edit) ───────────────────────────────────────────────────
  modalOpen: boolean
  // The id is set synchronously the moment the user clicks "Edit"; the full
  // coupon below comes from the on-open detail query so the form is always
  // hydrated with the freshest server-side shape.
  editingCouponId: string | null
  // `undefined` while the detail query is in-flight (or when the modal is for
  // Create); a real `LoyaltyCoupon` once the GET resolves.
  editingCoupon: LoyaltyCoupon | undefined
  isLoadingEditingCoupon: boolean
  openCreateModal: () => void
  openEditModal: (coupon: LoyaltyCoupon) => void
  closeModal: () => void

  // ── Mutations ───────────────────────────────────────────────────────────────
  saveCoupon: (draft: LoyaltyCouponDraft) => Promise<LoyaltyCoupon>
  deleteCoupon: (couponId: string) => Promise<void>
  isSaving: boolean
  isDeleting: boolean
}

export const LoyaltyCouponsContext = createContext<
  LoyaltyCouponsContextValue | undefined
>(undefined)
